import type {
  MbcAdminCheck,
  MbcAdminCriterion,
  MbcJudgedBriefField,
} from "@nlc/shared-types";

import {
  MBC_BRIEF_FIELD_LABELS,
  MBC_JUDGED_BRIEF_FIELDS,
} from "@nlc/shared-types";

import { Checkbox, CheckboxGroup } from "@/components/checkbox";
import { Link } from "@/components/link";

interface GuidelineChecksSectionProps {
  readonly checkKeys: string[];
  readonly checks: MbcAdminCheck[];
  readonly criteria: MbcAdminCriterion[];
  readonly onChange: (checkKeys: string[]) => void;
}

export function GuidelineChecksSection({
  checkKeys,
  checks,
  criteria,
  onChange,
}: GuidelineChecksSectionProps) {
  const criterionFields = new Set(criteria.map(({ briefField }) => briefField));
  const groups = MBC_JUDGED_BRIEF_FIELDS.filter((field) =>
    criterionFields.has(field),
  ).map((field) => ({
    checks: checks.filter((check) => check.field === field),
    field: field as MbcJudgedBriefField,
  }));

  return (
    <section className="p-6">
      <h2 className="text-md font-semibold text-text-primary">Checks</h2>
      <p className="mb-md text-sm text-text-tertiary">
        What the judge verifies in the brief for this guideline. Only fields
        with a criterion can have checks; manage the library in the{" "}
        <Link className="text-sm" href="/admin/mbc?tab=checks">
          Checks tab
        </Link>
        .
      </p>

      {groups.length === 0 ? (
        <p className="text-sm text-text-tertiary">
          Add a visual direction, text for design, assets or inspirations
          criterion to pick its checks.
        </p>
      ) : (
        <div className="flex flex-col gap-6">
          {groups.map(({ checks: fieldChecks, field }) => (
            <CheckboxGroup
              aria-label={`Checks for ${MBC_BRIEF_FIELD_LABELS[field]}`}
              key={field}
              onChange={onChange}
              value={checkKeys}
            >
              <h3 className="mb-sm text-sm font-semibold text-text-primary">
                {MBC_BRIEF_FIELD_LABELS[field]}
              </h3>
              {fieldChecks.length === 0 ? (
                <p className="text-sm text-text-tertiary">
                  No checks for this field yet.
                </p>
              ) : (
                fieldChecks.map(({ key, label }) => (
                  <Checkbox key={key} value={key}>
                    {label}
                  </Checkbox>
                ))
              )}
            </CheckboxGroup>
          ))}
        </div>
      )}
    </section>
  );
}
